import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { Loader, Card, EmptyState, PageHeader } from "@/components/ui-helpers";
import { toast } from "sonner";
import { MessageSquare, Send, AlertTriangle } from "lucide-react";

export default function Communications() {
  const [threads, setThreads] = useState(null);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [inbound, setInbound] = useState(false);
  const [channel, setChannel] = useState("WhatsApp");
  const endRef = useRef();

  const loadThreads = () => api.get("/messages").then((r) => setThreads(r.data)).catch(() => setThreads([]));
  useEffect(() => { loadThreads(); }, []);
  const [sp] = useSearchParams();
  const wantClient = sp.get("client");
  useEffect(() => {
    if (!wantClient || !threads || active?.client_id === wantClient) return;
    const t = threads.find((x) => x.client_id === wantClient);
    if (t) openThread(t);
    else api.get(`/clients/${wantClient}`).then((r) => openThread({ client_id: wantClient, client_name: r.data.full_name })).catch(() => toast.message("This item was removed"));
  }, [wantClient, threads]); // eslint-disable-line
  const openThread = (t) => {
    setActive(t);
    api.get(`/messages?client_id=${t.client_id}`).then((r) => { setMessages(r.data); loadThreads(); }).catch(() => setMessages([]));
  };
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!body.trim() || !active) return;
    try {
      await api.post("/messages", inbound ? { client_id: active.client_id, body, inbound: true, channel } : { client_id: active.client_id, body });
      setBody(""); const r = await api.get(`/messages?client_id=${active.client_id}`); setMessages(r.data);
      if (inbound) { toast.success("Client message logged — AI triage runs in a moment"); setTimeout(() => api.get(`/messages?client_id=${active.client_id}`).then((x) => setMessages(x.data)).catch(() => {}), 2500); }
    }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!threads) return <Loader />;
  return (
    <div>
      <PageHeader title="Client Messages" subtitle="Your message history with clients (WhatsApp / email log)." />
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-2 md:col-span-1 max-h-[70vh] overflow-y-auto">
          {threads.length === 0 ? <div className="p-6"><EmptyState icon={MessageSquare} title="No conversations" description="Messages with clients will appear here." /></div> : threads.map((t) => (
            <button key={t.client_id} data-testid={`thread-${t.client_id}`} onClick={() => openThread(t)} className={`w-full text-left px-3 py-3 rounded-xl flex items-center gap-3 ${active?.client_id === t.client_id ? "bg-primary/10" : "hover:bg-slate-50"}`}>
              <div className="h-9 w-9 rounded-full bg-secondary text-white flex items-center justify-center font-bold text-sm shrink-0">{t.client_name?.[0]}</div>
              <div className="flex-1 min-w-0"><div className="font-semibold text-slate-800 truncate">{t.client_name}</div><div className="text-[11px] text-slate-400">{(t.last || "").slice(0, 10)}</div></div>
              {t.urgent > 0 && <span data-testid={`thread-urgent-${t.client_id}`} title="Urgent client messages" className="bg-rose-100 text-rose-700 text-[10px] font-bold rounded-full px-2 py-0.5 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{t.urgent}</span>}
              {t.unread > 0 && <span className="bg-primary text-white text-[10px] font-bold rounded-full px-2 py-0.5">{t.unread}</span>}
            </button>
          ))}
        </Card>
        <Card className="md:col-span-2 flex flex-col h-[70vh]">
          {!active ? <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">Select a conversation</div> : (
            <>
              <div className="px-5 py-3 border-b border-slate-100 font-head font-bold text-slate-900">{active.client_name}</div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.to_office ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${m.to_office ? "bg-slate-100 text-slate-800" : "bg-primary text-white"}`}>
                      <div className="text-[10px] opacity-70 mb-0.5">{m.sender_name} · {(m.created_at || "").slice(0, 16).replace("T", " ")}{m.channel ? ` · via ${m.channel} (logged by ${m.logged_by})` : ""}</div>
                      {m.body}
                      {m.ai_triage && (
                        <div className="mt-1.5 flex gap-1.5 flex-wrap" data-testid={`msg-triage-${m.id}`}>
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/80 text-slate-700 border border-slate-200">{m.ai_triage.category}</span>
                          {m.ai_triage.urgent && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Urgent</span>}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="px-3 pt-2 flex items-center gap-3 text-xs text-slate-500">
                <label className="inline-flex items-center gap-1.5"><input data-testid="msg-inbound-toggle" type="checkbox" checked={inbound} onChange={(e) => setInbound(e.target.checked)} /> Log a message received from the client</label>
                {inbound && <select data-testid="msg-channel" value={channel} onChange={(e) => setChannel(e.target.value)} className="px-2 py-1 rounded-lg border border-slate-200">{["WhatsApp", "Phone", "Email", "In person", "Other"].map((c) => <option key={c}>{c}</option>)}</select>}
              </div>
              <div className="p-3 flex gap-2">
                <input data-testid="msg-input" value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={inbound ? "Paste or type what the client said..." : "Log a message to the client..."} className="flex-1 px-3 py-2.5 rounded-xl border border-slate-200 text-sm" />
                <button data-testid="msg-send" onClick={send} className="bg-primary text-white px-4 rounded-xl"><Send className="h-4 w-4" /></button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
